import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Alert } from "../../components/Alert";
import { RequirePermission } from "../../components/crm/RequirePermission";
import { Spinner } from "../../components/Spinner";
import { fetchCrmStats, isApiError } from "../../lib/crm-api";
import type { CrmStats } from "../../lib/crm-types";
import { formatMoney } from "../../lib/format";
import { useConfig } from "../../state/config-context";

function CrmDashboardInner(): ReactNode {
  const { t, lang } = useConfig();
  const [stats, setStats] = useState<CrmStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setStats(await fetchCrmStats());
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const locale = lang === "es" ? "es-ES" : "en-US";
  const cards: { key: string; label: string; value: string }[] =
    stats === null
      ? []
      : [
          { key: "companies", label: t("crm.dashboard.companies"), value: String(stats.companies) },
          { key: "contacts", label: t("crm.dashboard.contacts"), value: String(stats.contacts) },
          { key: "open", label: t("crm.dashboard.openDeals"), value: String(stats.deals_by_status.open) },
          { key: "won", label: t("crm.dashboard.wonDeals"), value: String(stats.deals_by_status.won) },
          { key: "lost", label: t("crm.dashboard.lostDeals"), value: String(stats.deals_by_status.lost) },
          {
            key: "value",
            label: t("crm.dashboard.pipelineValue"),
            value: formatMoney(stats.open_pipeline_value_minor, "USD", locale),
          },
          {
            key: "activities",
            label: t("crm.dashboard.pendingActivities"),
            value: String(stats.activities_pending),
          },
        ];

  return (
    <section className="page" aria-labelledby="crm-dashboard-title">
      <header className="page__header">
        <h1 id="crm-dashboard-title">{t("crm.dashboard.title")}</h1>
        <p className="muted">{t("crm.dashboard.subtitle")}</p>
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}
      {loading ? <Spinner label={t("app.loading")} /> : null}

      {!loading && stats !== null ? (
        <div className="card-grid">
          {cards.map((card) => (
            <article className="card" key={card.key}>
              <h2 className="card__title">{card.label}</h2>
              <p className="card__value">{card.value}</p>
            </article>
          ))}
        </div>
      ) : null}
    </section>
  );
}

export function CrmDashboardPage(): ReactNode {
  return (
    <RequirePermission permission="crm.deal.read">
      <CrmDashboardInner />
    </RequirePermission>
  );
}
